/**
 * lib/billing/engines/telangana.ts — TGSPDCL HT Billing Engine
 * =============================================================
 * Builds a BillBreakdown from telemetry rows using TGSPDCL HT Category 1A rules.
 * Receives the tariff config as a parameter — no direct tariff imports.
 *
 * Key differences: energy is kVAh not kWh; 3-tier rate structure; no PF/LF rebates.
 * Formula reference: VIREON-CORTEX Variables & Formulas PDF §3, §4.
 */

import type { TelemetryRow } from '@/types';
import type { StateTariff, ToDPeriod } from '@/tariffs';
import type { BillBreakdown, BillPeriodBreakdown } from '../types';
import { energyDelivered, max, safe, isValidPfReading } from '@/lib/calculations';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function weightedPF(rows: TelemetryRow[]): number {
  if (rows.length < 2) {
    const valid = rows.filter(isValidPfReading);
    if (!valid.length) return 0;
    return Math.min(valid.reduce((s, r) => s + safe(r.pfAvg), 0) / valid.length, 0.999);
  }
  const sorted   = [...rows].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  const deltaKwh  = Math.max(0, safe(sorted[sorted.length-1].energyWh)  - safe(sorted[0].energyWh))  / 1000;
  const deltaKvah = Math.max(0, safe(sorted[sorted.length-1].energyVAh) - safe(sorted[0].energyVAh)) / 1000;
  if (deltaKvah > 0) return Math.min(deltaKwh / deltaKvah, 1);
  const valid = rows.filter(isValidPfReading);
  if (!valid.length) return 0;
  return Math.min(valid.reduce((s, r) => s + safe(r.pfAvg), 0) / valid.length, 0.999);
}

function periodRows(rows: TelemetryRow[], period: ToDPeriod): TelemetryRow[] {
  return rows.filter(r => r.todPeriod === period).sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
}

/** kVAh delivered in a window via VAh register deltas. */
function kvahDelivered(rows: TelemetryRow[]): number {
  if (rows.length < 2) return 0;
  const first = rows[0];
  const last  = rows[rows.length-1];
  return Math.max(0, safe(last.energyVAh) - safe(first.energyVAh)) / 1000;
}

// ─── Engine ───────────────────────────────────────────────────────────────────

export function buildTelanganaBreakdown(
  rows:                      TelemetryRow[],
  tariff:                    StateTariff,
  daysPassed?:               number,
  daysTotal?:                number,
  contractDemandKvaOverride?: number,
): BillBreakdown {
  const CD    = contractDemandKvaOverride ?? tariff.defaultContractDemandKva;
  const minCD = CD * tariff.minDemandPct;

  const EMPTY: BillBreakdown = {
    spanHours: 0, monthFraction: 0,
    periods: [], ecTotalRs: 0, totalKvah: 0, totalKwh: 0,
    pfWeighted: 0, pfAdjTotalRs: 0,
    lfPct: 0, lfRebateRs: 0,
    peakKva: 0, mdKvaNormal: 0, mdKvaPeak: 0, mdKvaOffpeak: 0,
    chargeableKva: Math.round(minCD), dcRs: 0, dcPenalRs: 0, dcTotalRs: 0,
    netBeforeEdRs: 0, electricityDutyRs: 0, meterRentalRs: 0, customerChargesRs: 0,
    grossPayableRs: 0, tprRs: 0, lpscRs: 0, netPayableRs: 0,
    rateTierLabel: tariff.getTierLabel(0, CD),
    todPeakKvah: 0, todOffpeakKvah: 0,
    ecBaseRs: 0, todPeakPenaltyRs: 0, todOffpeakRebateRs: 0,
    normalKva: Math.round(minCD), addlKva: 0, dcNormalRs: 0, mdPenaltyRs: 0,
    subTotalRs: 0, grossPayable: 0,
  };

  if (rows.length < 2) return EMPTY;

  const sorted        = [...rows].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  const spanHours     = (sorted[sorted.length-1].timestamp.getTime() - sorted[0].timestamp.getTime()) / 3_600_000;
  const monthFraction = spanHours / (30 * 24);
  const todPeriods: ToDPeriod[] = ['NORMAL', 'PEAK', 'OFF-PEAK'];
  const pfWtd         = weightedPF(sorted);

  // ── Max demand & rate tier ────────────────────────────────────────────────
  const latestRow  = sorted[sorted.length-1];
  const peakKva    = Math.max(safe(latestRow.maxKva), safe(latestRow.maxDemandKva), max(sorted.map(r => safe(r.kvaTotal))));
  const rates      = tariff.getTariffRates(peakKva, CD);

  // ── Per-period kVAh breakdown ─────────────────────────────────────────────
  let totalKvah = 0;
  const periods: BillPeriodBreakdown[] = todPeriods.map(p => {
    const pr             = periodRows(sorted, p);
    const kvah           = kvahDelivered(pr);
    totalKvah           += kvah;
    const rateRsKvah     = rates[p];
    const energyChargeRs = kvah * rateRsKvah;
    return {
      period: p, energyConsumed: kvah, rateRsPerUnit: rateRsKvah, energyChargeRs,
      pfAdjPct: 0, pfAdjRs: 0,
      pfAvg: 0,  // TGSPDCL does not use per-period PF; overall pfWeighted used instead
      kwh: energyDelivered(pr), kvah,
      rateRsKwh: rateRsKvah, rateRsKvah,
    };
  });

  const ecTotalRs = periods.reduce((s, p) => s + p.energyChargeRs, 0);

  // ── TGSPDCL billing model: Base EC + ToD Peak Penalty − ToD Off-Peak Rebate ──
  const normalRate         = rates['NORMAL'];
  const peakPeriod         = periods.find(p => p.period === 'PEAK');
  const offpeakPeriod      = periods.find(p => p.period === 'OFF-PEAK');
  const todPeakKvah        = peakPeriod?.kvah    ?? 0;
  const todOffpeakKvah     = offpeakPeriod?.kvah  ?? 0;
  const ecBaseRs           = totalKvah * normalRate;
  const todPeakPenaltyRs   = todPeakKvah    * (rates['PEAK']     - normalRate);
  const todOffpeakRebateRs = todOffpeakKvah * (normalRate - rates['OFF-PEAK']);

  // ── Demand charges (TGSPDCL HT: normal + penal) ───────────────────────────
  // Normal kVA = max(min(peakKva, CD), 80%×CD), rounded
  const normalKva   = Math.round(Math.max(Math.min(peakKva, CD), minCD));
  // Penal kVA = max(0, peakKva − CD), rounded
  const addlKva     = Math.round(Math.max(0, peakKva - CD));
  const dcRs        = normalKva * tariff.demandChargeRsKva;
  const dcPenalRs   = addlKva   * tariff.demandPenalRsKva;
  const dcTotalRs   = dcRs + dcPenalRs;

  // ── Bill assembly (TGSPDCL spec) ──────────────────────────────────────────
  // Sub Total = Demand Charges (Normal + Penal) + Base EC + ToD adjustments
  //             + Electricity Duty + Colony + L&F + Green Energy + FSA
  //             (everything EXCEPT Customer Charges)
  // ED = flat Rs per kVAh
  const electricityDutyRs = totalKvah * tariff.electricityDutyValue;
  const subTotalRs        = ecTotalRs + dcTotalRs + electricityDutyRs;

  // Gross Total = Sub Total + Customer Charges (only addition here)
  // Customer charges = flat ₹3,500/month (TGSPDCL spec). NOT pro-rated by days
  // elapsed — it is a fixed monthly line item billed in full from day 1.
  const customerChargesRs = tariff.customerChargesRsMonth;
  const grossPayableRs    = subTotalRs + customerChargesRs;
  const lpscRs            = grossPayableRs * tariff.lpseFraction; // informational
  const netPayableRs      = grossPayableRs; // LPSC only if actually late

  // ── Projection ────────────────────────────────────────────────────────────
  let projectedEnergy: Record<ToDPeriod, number> | undefined;
  let projectedBillRs: number | undefined;

  if (daysPassed && daysPassed > 0 && daysTotal && daysTotal > daysPassed) {
    const rem = daysTotal - daysPassed;
    projectedEnergy = {} as Record<ToDPeriod, number>;
    let projEC = 0;
    for (const p of periods) {
      const projected = p.kvah + (p.kvah / daysPassed) * rem;
      projectedEnergy[p.period] = projected;
      projEC += projected * p.rateRsKvah;
    }
    const projTotal      = Object.values(projectedEnergy).reduce((s, v) => s + v, 0);
    const projED         = projTotal * tariff.electricityDutyValue;
    // Projected Sub Total: EC + Demand + ED (mirrors live subTotalRs formula)
    const projSubTotal   = projEC + dcTotalRs + projED;
    // Projected Gross Total: Sub Total + Customer Charges only
    projectedBillRs      = projSubTotal + tariff.customerChargesRsMonth;
  }

  return {
    spanHours, monthFraction,
    periods, ecTotalRs, totalKvah, totalKwh: 0,
    pfWeighted: pfWtd, pfAdjTotalRs: 0,
    lfPct: 0, lfRebateRs: 0,
    peakKva, mdKvaNormal: 0, mdKvaPeak: 0, mdKvaOffpeak: 0,
    chargeableKva: normalKva, dcRs, dcPenalRs, dcTotalRs,
    netBeforeEdRs: subTotalRs, electricityDutyRs, meterRentalRs: 0, customerChargesRs,
    grossPayableRs, tprRs: 0, lpscRs, netPayableRs,
    rateTierLabel: tariff.getTierLabel(peakKva, CD),
    // TGSPDCL ToD breakdown
    todPeakKvah, todOffpeakKvah,
    ecBaseRs, todPeakPenaltyRs, todOffpeakRebateRs,
    projectedBillRs, projectedEnergy,
    projectedKwh: undefined, projectedKvah: projectedEnergy,
    // Legacy aliases
    normalKva, addlKva, dcNormalRs: dcRs, mdPenaltyRs: dcPenalRs,
    subTotalRs, grossPayable: grossPayableRs,
  };
}